// Drives the outbox: watches connectivity, drains the queue when it can, and
// exposes the counts the UI needs to tell the shopkeeper what is still owed to
// the server.
//
// Mounted once, from Layout, which wraps every authenticated page.

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { drain, getFailed, pendingCount, subscribe } from "./outbox";

// Slow enough to be invisible on a metered connection, often enough that a
// shopkeeper who walks back into signal does not have to think about it.
const POLL_MS = 30_000;

export function useOutbox() {
  const { getAuthHeader, isAuthenticated } = useAuth();
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(pendingCount());
  const [failed, setFailed] = useState(getFailed().length);
  const [syncing, setSyncing] = useState(false);
  // Whether the last drain actually got through. navigator.onLine cannot tell
  // us this: a sleeping backend, a captive portal and a working connection all
  // report "online". Tracking the outcome lets the UI say which it is.
  const [reachable, setReachable] = useState(true);

  // Kept in a ref so the effect below does not re-subscribe on every render
  // just because getAuthHeader is a new closure each time.
  const authRef = useRef(getAuthHeader);
  authRef.current = getAuthHeader;

  const refresh = useCallback(() => {
    setPending(pendingCount());
    setFailed(getFailed().length);
  }, []);

  const syncNow = useCallback(async () => {
    if (!isAuthenticated || !navigator.onLine) return;
    setSyncing(true);
    try {
      const result = await drain(authRef.current);
      // "busy" means another drain is already running -- it says nothing about
      // the connection, so leave the current verdict alone.
      if (result && result.reason !== "busy") {
        setReachable(result.reason !== "unreachable");
      }
      return result;
    } finally {
      setSyncing(false);
      refresh();
    }
  }, [isAuthenticated, refresh]);

  useEffect(() => subscribe(refresh), [refresh]);

  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      // Assume the server is back until a drain proves otherwise; without this
      // the bar would stay stuck on the previous failure.
      setReachable(true);
      syncNow();
    };
    const goOffline = () => setOnline(false);

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    // navigator.onLine only means "there is a network interface", not "the
    // server is reachable" -- a captive portal or a dead backend both look
    // online. The interval and the focus handler are what actually recover
    // from those.
    const onFocus = () => syncNow();
    window.addEventListener("focus", onFocus);
    const timer = setInterval(syncNow, POLL_MS);

    syncNow(); // drain anything left from a previous session

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("focus", onFocus);
      clearInterval(timer);
    };
  }, [syncNow]);

  // What the shopkeeper actually needs to know: can work reach the server
  // right now, whatever the reason.
  const connected = online && reachable;

  return { online, reachable, connected, pending, failed, syncing, syncNow, refresh };
}
